import { useState, useEffect } from "react";
import { Phone, Upload, CheckCircle, MapPin, Clock, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const animalTypes = [
  "Raccoons",
  "Opossums",
  "Rats/Mice",
  "Wild Hogs",
  "Squirrels",
  "Bats",
  "Armadillos",
  "Moles",
  "Other",
];

const urgencyLevels = [
  { value: "emergency", label: "Emergency — Need help NOW" },
  { value: "urgent", label: "Urgent — Within 24 hours" },
  { value: "standard", label: "Standard — This week" },
  { value: "consultation", label: "Just need a consultation" },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    animal_type: "",
    urgency: "",
    message: "",
  });
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Get a Free Quote | Double D Trapping LLC | St. Augustine, FL";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.animal_type || !form.urgency) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("phone", form.phone);
      formData.append("address", form.address);
      formData.append("animal_type", form.animal_type);
      formData.append("urgency", form.urgency);
      formData.append("message", form.message);
      if (photo) formData.append("photo", photo);

      await axios.post(`${API}/quotes`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Quote request submitted! We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 904-237-6253.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="bg-[#0B110E] py-20 sm:py-28" data-testid="quote-success">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 bg-[#16A34A]/10 border border-[#16A34A]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#16A34A]" />
          </div>
          <h1 className="text-[#F8FAFC] font-black text-3xl sm:text-4xl tracking-tighter mb-4">
            Quote Request Received
          </h1>
          <p className="text-[#9CA3AF] text-base mb-8">
            D. Davis will review your request and get back to you shortly. For emergencies, call us directly.
          </p>
          <a href="tel:9042376253" data-testid="success-call-btn">
            <button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-black uppercase tracking-widest text-sm px-8 py-4 rounded-sm btn-glow inline-flex items-center gap-3 transition-colors duration-150">
              <Phone className="w-5 h-5" /> Call 904-237-6253
            </button>
          </a>
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Header */}
      <section className="bg-[#0F1A15] border-b border-[#1F2937] py-16 sm:py-20" data-testid="contact-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">Contact Us</p>
          <h1 className="text-[#F8FAFC] font-black text-4xl sm:text-5xl tracking-tighter mb-4" data-testid="contact-page-title">
            Get a Free Quote
          </h1>
          <p className="text-[#9CA3AF] text-base sm:text-lg max-w-2xl leading-relaxed">
            Fill out the form below and we'll get back to you fast. For emergencies, call <a href="tel:9042376253" className="text-[#D95D39] font-bold">904-237-6253</a> directly.
          </p>
        </div>
      </section>

      {/* Form + Sidebar */}
      <section className="bg-[#0B110E] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                      Your Name <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Input
                      data-testid="quote-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Full name"
                      className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] placeholder:text-[#64748B] h-12 rounded-sm focus-visible:ring-[#D95D39]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                      Phone Number <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Input
                      data-testid="quote-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(904) 000-0000"
                      type="tel"
                      className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] placeholder:text-[#64748B] h-12 rounded-sm focus-visible:ring-[#D95D39]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                    Service Address <span className="text-[#DC2626]">*</span>
                  </Label>
                  <Input
                    data-testid="quote-address"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder="Street address, city, state"
                    className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] placeholder:text-[#64748B] h-12 rounded-sm focus-visible:ring-[#D95D39]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                      Animal Type <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Select
                      value={form.animal_type}
                      onValueChange={(v) => setForm({ ...form, animal_type: v })}
                    >
                      <SelectTrigger
                        data-testid="quote-animal-type"
                        className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] h-12 rounded-sm focus:ring-[#D95D39]"
                      >
                        <SelectValue placeholder="Select animal type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F1A15] border-[#1F2937]">
                        {animalTypes.map((a) => (
                          <SelectItem
                            key={a}
                            value={a}
                            className="text-[#F8FAFC] focus:bg-[#1E293B] focus:text-[#F8FAFC]"
                            data-testid={`animal-option-${a.toLowerCase().replace(/[/ ]/g, '-')}`}
                          >
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                      Urgency Level <span className="text-[#DC2626]">*</span>
                    </Label>
                    <Select
                      value={form.urgency}
                      onValueChange={(v) => setForm({ ...form, urgency: v })}
                    >
                      <SelectTrigger
                        data-testid="quote-urgency"
                        className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] h-12 rounded-sm focus:ring-[#D95D39]"
                      >
                        <SelectValue placeholder="How urgent?" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F1A15] border-[#1F2937]">
                        {urgencyLevels.map((u) => (
                          <SelectItem
                            key={u.value}
                            value={u.value}
                            className="text-[#F8FAFC] focus:bg-[#1E293B] focus:text-[#F8FAFC]"
                            data-testid={`urgency-option-${u.value}`}
                          >
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                    Additional Details
                  </Label>
                  <Textarea
                    data-testid="quote-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Describe what you're seeing or hearing. The more detail, the better."
                    rows={4}
                    className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] placeholder:text-[#64748B] rounded-sm focus-visible:ring-[#D95D39]"
                  />
                </div>

                <div>
                  <Label className="text-[#F8FAFC] text-sm font-semibold mb-2 block">
                    Upload Photo (Optional)
                  </Label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPhoto(e.target.files[0] || null)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      data-testid="quote-photo"
                    />
                    <div className="border border-dashed border-[#1F2937] rounded-sm p-6 text-center bg-[#0F1A15] hover:border-[#D95D39]/50 transition-colors duration-150">
                      <Upload className="w-6 h-6 text-[#64748B] mx-auto mb-2" />
                      <p className="text-[#9CA3AF] text-sm">
                        {photo ? photo.name : "Click or drag photo here"}
                      </p>
                      <p className="text-[#64748B] text-xs mt-1">JPG, PNG, WebP up to 10MB</p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D95D39] hover:bg-[#c04d2d] disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm py-4 rounded-sm btn-glow transition-colors duration-150"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Quote Request"}
                </button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="border border-[#1F2937] rounded-sm p-6 bg-[#0F1A15]" data-testid="contact-sidebar">
                <h3 className="text-[#F8FAFC] font-bold text-lg mb-5">Contact Info</h3>
                <div className="space-y-4">
                  <a href="tel:9042376253" className="flex items-center gap-3 text-[#D95D39] font-bold text-lg hover:underline">
                    <Phone className="w-5 h-5" /> 904-237-6253
                  </a>
                  <div className="flex items-start gap-3 text-[#9CA3AF] text-sm">
                    <MapPin className="w-5 h-5 shrink-0 mt-0.5" />
                    <span>St. Augustine, FL<br />St. Johns & Flagler Counties</span>
                  </div>
                  <div className="flex items-center gap-3 text-[#9CA3AF] text-sm">
                    <Clock className="w-5 h-5 shrink-0" />
                    <span>Available 24/7</span>
                  </div>
                </div>
              </div>

              <div className="border border-[#D95D39]/20 rounded-sm p-6 bg-[#D95D39]/5">
                <h3 className="text-[#F8FAFC] font-bold text-sm mb-3">Emergency?</h3>
                <p className="text-[#9CA3AF] text-sm mb-4">
                  Don't wait for a form response. Call us right now — D. Davis picks up.
                </p>
                <a href="tel:9042376253">
                  <button className="w-full bg-[#D95D39] hover:bg-[#c04d2d] text-white font-bold uppercase tracking-wider text-xs py-3 rounded-sm btn-glow transition-colors duration-150">
                    Call Now
                  </button>
                </a>
              </div>

              <div className="border border-[#1F2937] rounded-sm p-6 bg-[#0F1A15]">
                <div className="space-y-3">
                  {[
                    "50 Years Experience",
                    "Licensed & Insured",
                    "Federal Swine Dealers License",
                    "100% Guaranteed",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#9CA3AF] text-sm">
                      <Shield className="w-4 h-4 text-[#D95D39] shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
